/**
 * Created by vlafed on 24.08.17.
 * Модуль для управления ограничениями доступа на основе ролей, проектов и пользователей.
 * 
 * Правила доступа:
 * - allow: массив ролей, которым разрешен доступ
 * - deny: массив ролей, которым запрещен доступ
 * - project: массив ID проектов, для которых действует правило (если не указано - для всех)
 * - users_allow: массив ID пользователей-исключений, которым разрешен доступ
 * - users_deny: массив ID пользователей-исключений, которым запрещен доступ
 */
define([], function () {
    const projects = {
        alpikaProd: "8a8ae5be8f3deac0018fb92bbbc4102d",
        alpikaTest: "8a8ae59c91a35d980191a3c4b8620000"
    };
    const users = {
        mariupiltrans: '8a8ae5be94e0dc7c0194efab672b01c4',
        test_mariupiltrans: '8a8ae59c99a42140019a0b6f581105f3',
    };
    const accessRules = {
        restrictAdmin: {
            allow: ['admin', 'superadmin', 'enterprise_admin'],
        },
        restrictMariupiltrans: {
            allow: ['superadmin'],
            users_allow: [users.mariupiltrans, users.test_mariupiltrans]
        },
        restrictAdSa: {
            allow: ['admin', 'superadmin'],
            // deny: ['enterprise_admin']
        },
        restrictSa: {
            allow: ['superadmin'],
            // deny: ['admin', 'enterprise_admin'],
            // users_allow: ['specific_user_id'] // исключения для конкретных пользователей
        },
        restrictAdAlpika: {
            // ВАЖНО: Это правило используется для ЗАПРЕТА доступа админу в проектах Alpika
            // Логика обратная: если правило найдено - доступ запрещен
            // Поэтому здесь указано allow, но фактически это означает "запретить этим ролям"
            allow: ['admin'],
            // deny: ['superadmin', 'enterprise_admin'],
            project: [projects.alpikaProd, projects.alpikaTest]
        },
        restrictOperatorAlpika: {
            allow: ['transporter'],
            // deny: ['superadmin', 'enterprise_admin', 'admin'],
            project: [projects.alpikaProd, projects.alpikaTest]
        },
        restrictAlpika: {
            // Это правило определяет, является ли пользователь из проекта Alpika
            // Возвращает true для всех ролей в проекте Alpika, кроме superadmin и enterprise_admin
            // ВАЖНО: Для этого правила используется специальная логика в функции restrictAlpika,
            // а не стандартный checkAccess, так как нужно вернуть true для всех в проекте, кроме deny
            deny: ['superadmin', 'enterprise_admin'],
            project: [projects.alpikaProd, projects.alpikaTest]
        }
    };

    /**
     * Проверяет доступ пользователя на основе правила доступа.
     * 
     * @param {Object|String} user - Объект пользователя {role, userId, projectId} или только роль (для обратной совместимости)
     * @param {String} ruleName - Имя правила из accessRules
     * @returns {Boolean} true если доступ разрешен, false если запрещен
     */
    const checkAccess = (user, ruleName) => {
        if (!user || !ruleName) {
            console.error("Invalid parameters: user and ruleName are required");
            return false;
        }

        const rule = accessRules[ruleName];
        if (!rule) {
            console.warn(`Rule '${ruleName}' not found, defaulting to false`);
            return false;
        }

        // Нормализация входных данных: поддержка как объекта пользователя, так и только роли
        const userRole = typeof user === 'string' ? user : user.role;
        const userId = typeof user === 'object' ? user.userId : null;
        const projectId = typeof user === 'object' ? user.projectId : null;

        // 1. Проверка исключений для конкретных пользователей (имеет наивысший приоритет)
        if (userId) {
            if (rule.users_allow && rule.users_allow.includes(userId)) {
                return true; // Пользователь в списке исключений - разрешить доступ
            }
            if (rule.users_deny && rule.users_deny.includes(userId)) {
                return false; // Пользователь в списке запрещенных - запретить доступ
            }
        }

        // 2. Проверка ограничения по проекту
        if (rule.project && projectId) {
            if (!rule.project.includes(projectId)) {
                return false; // Пользователь не в нужном проекте
            }
        }

        // 3. Проверка ролей (deny имеет приоритет над allow)
        if (userRole) {
            if (rule.deny && rule.deny.includes(userRole)) {
                return false; // Роль в списке запрещенных
            }
            if (rule.allow && rule.allow.includes(userRole)) {
                return true; // Роль в списке разрешенных
            }
        }

        // По умолчанию доступ запрещен
        return false;
    };

    /**
     * Вспомогательная функция для нормализации пользователя.
     * Поддерживает как объект пользователя, так и только роль (для обратной совместимости).
     */
    const normalizeUser = (user) => {
        if (typeof user === 'string') {
            return { role: user, userId: null, projectId: null };
        }
        return user;
    };

    return {
        /**
         * Новые функции с префиксом _ используют улучшенную логику checkAccess.
         * Они принимают объект пользователя {role, userId, projectId}.
         */

        _restrictAdmin: (user) => {
            // Отрицание: restrictAdmin означает "запретить админам"
            return !checkAccess(normalizeUser(user), 'restrictAdmin');
        },

        _restrictAdSa: (user) => {
            return checkAccess(normalizeUser(user), 'restrictAdSa');
        },

        _restrictSa: (user) => {
            return checkAccess(normalizeUser(user), 'restrictSa');
        },

        _restrictAdAlpika: (user) => {
            return checkAccess(normalizeUser(user), 'restrictAdAlpika');
        },

        /**
         * Публичные функции для использования в коде.
         * Поддерживают обратную совместимость.
         */

        restrictOperatorAlpika: (user) => {
            return checkAccess(normalizeUser(user), 'restrictOperatorAlpika');
        },

        isAlpikaProject: function (projectId) {
            return projectId === projects.alpikaProd || projectId === projects.alpikaTest;
        },

        restrictAlpika: (user) => {
            // Специальная логика: возвращает true, если пользователь в проекте Alpika
            // и его роль НЕ superadmin или enterprise_admin
            // Это используется для определения "является ли пользователь из Alpika"
            const userObj = normalizeUser(user);
            const role = userObj.role;
            const project = userObj.projectId;

            // Проверяем, что проект является одним из проектов Alpika
            if (project !== projects.alpikaProd && project !== projects.alpikaTest) {
                return false; // Если не проект Alpika - вернуть false
            }

            // Если проект Alpika, проверяем роль
            // Запрещаем только для superadmin и enterprise_admin
            if (role === 'superadmin' || role === 'enterprise_admin') {
                return false;
            }

            // Для всех остальных ролей (admin, transporter и т.д.) возвращаем true
            return true;
        },

        restrictMariupiltrans: (user) => {
            return checkAccess(normalizeUser(user), 'restrictMariupiltrans');
        },

        /**
         * Старые функции для обратной совместимости.
         * Принимают только роль (string) или объект пользователя.
         * Используют старую логику со switch-case для сохранения точного поведения.
         */

        restrictAdmin: function (role) {
            // Если передан объект пользователя, извлекаем роль
            if (typeof role === 'object' && role !== null) {
                role = role.role;
            }

            switch (role) {
                case 'admin':
                case 'superadmin':
                case 'enterprise_admin':
                    return false; // Запретить доступ для этих ролей
            }

            return true; // Разрешить доступ для остальных ролей
        },

        restrictAdSa: function (role) {
            // Если передан объект пользователя, извлекаем роль
            if (typeof role === 'object' && role !== null) {
                role = role.role;
            }

            switch (role) {
                case 'admin':
                case 'superadmin':
                    return false; // Запретить доступ для этих ролей
            }

            return true; // Разрешить доступ для остальных ролей
        },

        restrictSa: function (role) {
            // Если передан объект пользователя, извлекаем роль
            if (typeof role === 'object' && role !== null) {
                role = role.role;
            }

            switch (role) {
                case 'superadmin':
                    return false; // Запретить доступ для этой роли
            }

            return true; // Разрешить доступ для остальных ролей
        },

        restrictAdAlpika: function (user) {
            // Поддержка как объекта пользователя, так и только роли
            const userObj = normalizeUser(user);
            const role = userObj.role;
            const project = userObj.projectId;

            // Исправлен баг: должно быть && вместо ||
            // Проверяем, что проект НЕ является одним из проектов Alpika
            if (project !== projects.alpikaProd && project !== projects.alpikaTest) {
                return true; // Если не проект Alpika - разрешить доступ
            }

            // Если проект Alpika, проверяем роль
            switch (role) {
                case 'admin':
                    return false; // Запретить доступ админу в проектах Alpika
            }

            return true; // Разрешить доступ для остальных ролей в проектах Alpika
        },

        /**
         * Внутренние функции с префиксом _ (legacy).
         * Сохранены для обратной совместимости, но рекомендуется использовать новые версии.
         */

        _restrictOperatorAlpika: function (user) {
            const userObj = normalizeUser(user);
            const role = userObj.role;
            const project = userObj.projectId;

            // Проверяем, что проект является одним из проектов Alpika
            if (project !== projects.alpikaProd && project !== projects.alpikaTest) {
                return false; // Если не проект Alpika - запретить доступ
            }

            // Если проект Alpika, проверяем роль
            switch (role) {
                case 'admin':
                case 'superadmin':
                case 'enterprise_admin':
                    return false; // Запретить доступ для этих ролей
            }

            return true; // Разрешить доступ для остальных ролей (например, transporter)
        },

        _restrictAlpika: function (user) {
            const userObj = normalizeUser(user);
            const role = userObj.role;
            const project = userObj.projectId;

            // Проверяем, что проект является одним из проектов Alpika
            if (project !== projects.alpikaProd && project !== projects.alpikaTest) {
                return false; // Если не проект Alpika - запретить доступ
            }

            // Если проект Alpika, проверяем роль
            switch (role) {
                case 'admin':
                    return true; // Админ альпаки должен иметь ограничения как и водитель
                case 'superadmin':
                case 'enterprise_admin':
                    return false; // Запретить доступ для этих ролей
            }

            return true; // Разрешить доступ для остальных ролей (например, transporter)
        }
    };
});